import { MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { clientApi } from '@/config/axios';
import { ReferralWork } from './ReferralWork';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { Textarea } from '@/components/ui/textarea';
import { usePermissions } from '@/features/permissions';
import { InlineReferralReview } from './InlineReferralReview';
import { AssistantPreviewLink } from './workbench/PreviewLink';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

type Action = 'details' | 'checklist' | 'referrals';
type Answer = {
  message: string;
  checked_at: string;
  action: Action | 'referral_work' | null;
  truncated?: boolean;
  items: { label: string; text: string; evidence: { kind: string; record_id: string; version: string | number } }[];
};
type Props = {
  initiallyOpen?: boolean;
  patientId: string;
  patientName: string;
  onNavigate: (action: Action, jobId?: string) => void;
};

export function AssistantSheet(props: Props) {
  const { user, organization } = useAuth();
  const { can } = usePermissions();
  const org = organization?.organization_id;
  if (!org || !user || !can('use_assistant') || !can('read_patient')) return null;
  // Unmount all requests and ephemeral conversation when any security context changes.
  return <PatientAssistant key={`${org}:${user.id}:${props.patientId}`} {...props} org={org} />;
}

export function PatientAssistant({ org, patientId, onNavigate, patientName, initiallyOpen }: Props & { org: string }) {
  const [open, setOpen] = useState(initiallyOpen ?? false);
  const [showReferralWork, setShowReferralWork] = useState(false);
  const [reviewJob, setReviewJob] = useState<string | null>(null);
  const [chips, setChips] = useState<Record<string, string> | null>(null);
  const [text, setText] = useState('');
  const [turns, setTurns] = useState<{ question: string; answer: Answer }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const base = `/api/v1/organizations/${org}/assistant`;
  useEffect(() => {
    const abort = new AbortController();
    clientApi
      .get(`${base}/capabilities`, { signal: abort.signal })
      .then(({ data }) => {
        if (!abort.signal.aborted && data.enabled) setChips(data.chips);
      })
      .catch(() => {});
    return () => {
      abort.abort();
      request.current?.abort();
    };
  }, [base]);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'nearest' });
  }, [turns, busy]);

  async function send(chip?: string) {
    if (request.current || (!chip && !text.trim())) return;
    const abort = new AbortController();
    request.current = abort;
    setBusy(true);
    setError('');
    const question = chip ? chips![chip] : text.trim();
    try {
      const { data } = await clientApi.post<Answer>(
        `${base}/patients/${patientId}/messages`,
        {
          request_id: crypto.randomUUID(),
          ...(chip ? { chip_key: chip } : { text: question }),
        },
        { signal: abort.signal },
      );
      if (!abort.signal.aborted) {
        setTurns((previous) => [...previous.slice(-9), { question, answer: data }]);
        setText('');
        if (data.action === 'referral_work') setShowReferralWork(true);
      }
    } catch (cause) {
      if (!abort.signal.aborted) {
        const status = (cause as { response?: { status?: number } }).response?.status;
        if (status === 403 || status === 404) setTurns([]);
        setError(
          status === 429
            ? 'Daily assistant limit reached. Use the chart directly.'
            : status === 403 || status === 404
              ? 'This patient or assistant is no longer accessible.'
              : 'The assistant could not complete this request. Try a suggested question or use the chart directly.',
        );
      }
    } finally {
      if (!abort.signal.aborted) {
        request.current = null;
        setBusy(false);
      }
    }
  }
  if (!chips) return null;
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm" variant="outline">
          <MessageCircle data-icon="inline-start" />
          Assistant
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Patient assistant</SheetTitle>
          <SheetDescription>{patientName} · Review before applying</SheetDescription>
        </SheetHeader>
        <AssistantPreviewLink />
        <p className="px-4 text-sm text-muted-foreground">
          Ask about the EMR, labs, checklist tasks, or prepare a linked referral for review. Starting processing does
          not change the chart. This conversation lasts only while this chart is open; each question is handled
          independently.
        </p>
        <div aria-label="Assistant conversation" className="min-h-0 flex-1 overflow-y-auto px-4">
          {showReferralWork ? (
            <>
              <Button variant="ghost" onClick={() => setShowReferralWork(false)}>
                Back to conversation
              </Button>
              {open && reviewJob ? (
                <InlineReferralReview org={org} jobId={reviewJob} onBack={() => setReviewJob(null)} />
              ) : (
                open && (
                  <ReferralWork
                    base={`${base}/patients/${patientId}`}
                    onReview={(jobId) => {
                      setReviewJob(jobId);
                    }}
                  />
                )
              )}
            </>
          ) : (
            turns.map((turn, index) => (
              <article key={index} className="mb-4 flex flex-col gap-3 border-b pb-4">
                <p className="font-medium">{turn.question}</p>
                <p>{turn.answer.message}</p>
                <p className="text-xs text-muted-foreground">
                  Checked {new Date(turn.answer.checked_at).toLocaleString()}
                </p>
                {turn.answer.items.map((item, i) => (
                  <div key={i} className="rounded-md border p-3 text-sm">
                    <p className="font-medium">{item.label}</p>
                    <p className="whitespace-pre-wrap break-words">{item.text}</p>
                    <details className="mt-2 text-xs text-muted-foreground">
                      <summary>
                        Source:{' '}
                        {item.evidence.kind === 'guide'
                          ? 'Application guide'
                          : item.evidence.kind === 'lab'
                            ? 'Recorded lab result'
                            : 'Patient checklist'}
                      </summary>
                      <p className="break-all">
                        Record {item.evidence.record_id} · Version {item.evidence.version}
                      </p>
                    </details>
                  </div>
                ))}
                {turn.answer.truncated && <p className="text-sm">More records are available in the chart.</p>}
                {turn.answer.action && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (turn.answer.action === 'referral_work') setShowReferralWork(true);
                      else {
                        setOpen(false);
                        onNavigate(turn.answer.action!);
                      }
                    }}>
                    {turn.answer.action === 'referral_work'
                      ? 'Choose referral source'
                      : turn.answer.action === 'details'
                        ? 'Open maternal flowsheet area'
                        : turn.answer.action === 'checklist'
                          ? 'Open checklist'
                          : 'Open referral inbox'}
                  </Button>
                )}
              </article>
            ))
          )}
          {busy && <p role="status">Checking your request…</p>}
          {error && (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          )}
          <div ref={end} />
        </div>
        {!showReferralWork && (
          <div className="flex flex-col gap-3 border-t p-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(chips).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  disabled={busy}
                  variant="outline"
                  className="h-auto whitespace-normal text-left"
                  onClick={() => void send(key)}>
                  {label}
                </Button>
              ))}
            </div>
            <form
              className="flex flex-col gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                void send();
              }}>
              <label htmlFor="assistant-question" className="text-sm font-medium">
                Your question
              </label>
              <Textarea
                id="assistant-question"
                value={text}
                disabled={busy}
                maxLength={2000}
                placeholder="How do I review this patient's labs?"
                onChange={(event) => setText(event.target.value)}
              />
              <div className="flex justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy || !turns.length}
                  onClick={() => {
                    setTurns([]);
                    setError('');
                  }}>
                  Clear conversation
                </Button>
                <Button type="submit" disabled={busy || !text.trim()}>
                  Ask assistant
                </Button>
              </div>
            </form>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
